import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { plural } from './AdminUi';
import NotifySheet from './NotifySheet';

// „Pošalji obavijest” iz detalja zadatka: primatelji po skupinama (dodijeljeni, grupe zadatka, a admin i svi
// članovi) uz pojedinačno odznačavanje; poruka s preporučenim tekstovima. Uvijek in-app + push (backend).

const DAYS = ['ned', 'pon', 'uto', 'sri', 'čet', 'pet', 'sub'];
const hm = (d) => d.toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' });

function whenText(iso) {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const diff = Math.round((day - today) / 86400000);
  if (diff === 0) return `danas u ${hm(d)}`;
  if (diff === 1) return `sutra u ${hm(d)}`;
  return `${DAYS[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}. u ${hm(d)}`;
}

export default function TaskNotifySheet({ task, user, onClose, onSent }) {
  const isAdmin = user.role === 'admin';
  const me = user.email;
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [last, setLast] = useState(null);
  const [opts, setOpts] = useState({ assigned: true, groups: true });

  useEffect(() => {
    api.get('/groups').then(r => setGroups(r.data)).catch(() => {});
    if (isAdmin) api.get('/users').then(r => setUsers(r.data)).catch(() => {});
    api.get(`/tasks/${task.id}/notify/last`).then(r => setLast(r.data)).catch(() => {});
    api.get(`/tasks/${task.id}/notify/options`).then(r => setOpts(r.data)).catch(() => {});
  }, [task.id, isAdmin]);

  // Skupine primatelja (redoslijed = prioritet pri razvrstavanju osobe koja je u više skupina).
  const sources = useMemo(() => {
    const list = [];
    const assigned = (task.assignments || []).filter(a => a.status !== 'declined' && a.email !== me)
      .map(a => ({ email: a.email, name: a.full_name || a.email, sub: a.status === 'accepted' ? 'sudjeluje' : 'pozvan/a' }));
    if (opts.assigned && assigned.length) list.push({ key: 'assigned', label: 'Dodijeljeni', people: assigned });
    for (const tg of (opts.groups ? task.groups || [] : [])) {
      const g = groups.find(x => x.id === tg.id);
      if (!g) continue;
      const people = (g.members || []).filter(m => m.email !== me)
        .map(m => ({ email: m.email, name: m.full_name || m.email, sub: m.role === 'owner' ? 'vlasnik grupe' : null }));
      if (people.length) list.push({ key: `g:${g.id}`, label: `Grupa ${g.name}`, people });
    }
    if (isAdmin && users.length) {
      list.push({ key: 'all', label: 'Svi članovi', people: users.filter(u => u.email !== me).map(u => ({ email: u.email, name: u.full_name || u.email, sub: null })) });
    }
    return list;
  }, [task, groups, users, isAdmin, me, opts]);

  // Početno: dodijeljeni ako ih ima, inače grupe zadatka.
  const defaultSrc = useMemo(() => (sources.some(s => s.key === 'assigned')
    ? ['assigned'] : sources.filter(s => s.key.startsWith('g:')).map(s => s.key)), [sources]);

  // Preporučeni tekstovi prema stanju zadatka.
  const templates = useMemo(() => {
    const accepted = (task.assignments || []).filter(a => a.status === 'accepted').length;
    const missing = Math.max(0, (task.required_people || 1) - accepted);
    const list = [];
    if (missing > 0 && task.status !== 'done') {
      list.push({ key: 'missing', label: 'Fali ljudi', text: `Na zadatku fali još ${missing} ${plural(missing, 'osoba', 'osobe', 'osoba')}. Javi se ako možeš.` });
    }
    list.push({ key: 'reminder', label: 'Podsjetnik', text: `Podsjetnik: ${task.title} je ${whenText(task.start_date)}.` });
    list.push({ key: 'change', label: 'Promjena', text: 'Promjena: ' });
    list.push({ key: 'thanks', label: 'Hvala', text: 'Hvala svima na sudjelovanju!' });
    return list;
  }, [task]);

  return (
    <NotifySheet
      subtitle={`${task.title} · ${whenText(task.start_date)}`}
      sources={sources}
      defaultSrc={defaultSrc}
      templates={templates}
      last={last}
      lastLabel="Zadnja obavijest za ovaj zadatak"
      emptyText="Zadatak nema dodijeljenih ni grupu, pa nema kome poslati."
      note={<>Primatelji je vide u Obavijestima, a tko ima uključen push dobit će je i na mobitel. Dodir na obavijest otvara ovaj zadatak.
        {!isAdmin && ' Kao vlasnik grupe šalješ dodijeljenima i članovima grupa zadatka.'}</>}
      onSend={async (emails, message, audience) => (await api.post(`/tasks/${task.id}/notify`, { emails, message, audience })).data.sent}
      onSent={onSent}
      onClose={onClose}
    />
  );
}
